import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    MdChevronRight, 
    MdBusiness, 
    MdPerson, 
    MdEmail, 
    MdPhone,
    MdCalendarToday,
    MdLocalShipping,
    MdLocationOn,
    MdCheckCircle,
    MdRadioButtonChecked,
    MdArrowBack
} from 'react-icons/md';
import styles from './PODetailView.module.css';
import Loader from '../components/common/Loader';

const PODetailView = () => {
    const { poNumber } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Simulate loading
        const timer = setTimeout(() => setLoading(false), 800);
        return () => clearTimeout(timer);
    }, []);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <Loader text="Loading purchase order details..." />
            </div>
        );
    }

    return (
        <div className={styles['add-project-page']}>
            {/* Page Header - Aligned with Project Theme */}
            <div className={styles['page-header']}>
                <button onClick={() => navigate(-1)} className="back-btn">
                    <MdArrowBack size={20} />
                </button>
                <div className={styles['po-title-section']}>
                    <h1>#{poNumber} <span className={`${styles['status-badge']} ${styles['status-pending']}`}>Pending</span></h1>
                    <div className={styles['po-meta-info']}>
                        Created on Oct 24, 2023 • Houston, TX
                    </div>
                </div>
            </div>

            <div className={styles['detail-grid']}>
                {/* Left Column */}
                <div className={styles['main-content']}>
                    {/* Vendor Details Section */}
                    <div className={styles['form-section']}>
                        <h3>Vendor Details</h3>
                        <div className={styles['vendor-card-content']}>
                            <div className={styles['vendor-logo']}>
                                <MdBusiness size={32} />
                            </div>
                            <div className={styles['vendor-info']}>
                                <h4>Nexus Industries Ltd</h4>
                                <p className={styles['vendor-address']}>
                                    Industrial Park North, 42nd St, Houston, TX 77001, USA
                                </p>
                                <div className={styles['vendor-contacts']} style={{ marginTop: '16px' }}>
                                    <div className={styles['contact-item']}>
                                        <MdPerson size={18} style={{ color: '#4f46e5' }} /> Sarah Jenkins (Account Lead)
                                    </div>
                                    <div className={styles['contact-item']}>
                                        <MdEmail size={18} style={{ color: '#4f46e5' }} /> s.jenkins@nexus-ind.com
                                    </div>
                                    <div className={styles['contact-item']}>
                                        <MdPhone size={18} style={{ color: '#4f46e5' }} /> +1 (555) 012-9938
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Line Items Section */}
                    <div className={styles['form-section']} style={{ padding: 0, overflow: 'hidden' }}>
                        <div className={styles['section-header-with-badge']}>
                            <h3>Line Items</h3>
                            <span className={styles['count-badge']}>2 ITEMS</span>
                        </div>
                        <div className={styles['table-container']}>
                            <table className={styles['items-table']}>
                                <thead>
                                    <tr>
                                        <th>Item & Description</th>
                                        <th style={{ textAlign: 'center' }}>Qty</th>
                                        <th>Unit Price</th>
                                        <th style={{ textAlign: 'right' }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <div className={styles['item-info-wrapper']}>
                                                <span className={styles['item-primary-text']}>Industrial Bearings (Steel-X)</span>
                                                <span className={styles['item-secondary-text']}>Grade A heavy-duty rotary assembly for X-Series industrial machines</span>
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>240</td>
                                        <td>₹45.00</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#111827' }}>₹10,800.00</td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div className={styles['item-info-wrapper']}>
                                                <span className={styles['item-primary-text']}>High-Tensile Steel Rods</span>
                                                <span className={styles['item-secondary-text']}>4-meter length, structural grade, anti-corrosive coating</span>
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>500</td>
                                        <td>₹125.00</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#111827' }}>₹62,500.00</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className={styles['summary-section']}>
                            <div className={styles['summary-table']}>
                                <div className={`${styles['summary-row']} ${styles['total']}`}>
                                    <span>Total Value</span>
                                    <span>₹79,797.25</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column (Sidebar) */}
                <div className={styles['sidebar']}>
                    {/* General Information Section */}
                    <div className={styles['form-section']}>
                        <h3>General Information</h3>
                        <div className={styles['sidebar-info-row']}>
                            <div className={styles['info-group']}>
                                <label>Requester</label>
                                <div className={styles['info-value']}>
                                    <div className={styles['requester-avatar']}>AM</div>
                                    Alexander Miller
                                </div>
                            </div>
                            <div className={styles['info-group']}>
                                <label>Date Created</label>
                                <div className={styles['info-value']}>
                                    Oct 24, 2023 - 09:42 AM
                                </div>
                            </div>
                            <div className={styles['info-group']}>
                                <label>Expected Delivery</label>
                                <div className={styles['info-value']} style={{ color: '#4f46e5', fontWeight: 600 }}>
                                    <MdCalendarToday size={18} /> Nov 02, 2023
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Shipping & Logistics Section */}
                    <div className={styles['form-section']}>
                        <h3>Shipping & Logistics</h3>
                        <div className={styles['sidebar-info-row']}>
                            <div className={styles['info-group']}>
                                <label>Method</label>
                                <div className={styles['info-value']}>
                                    <MdLocalShipping size={18} style={{ color: '#4f46e5' }} /> Express Ground
                                </div>
                            </div>
                            <div className={styles['info-group']}>
                                <label>Destination</label>
                                <div className={styles['info-value']}>
                                    <MdLocationOn size={18} style={{ color: '#4f46e5' }} /> Central Warehouse A-12
                                </div>
                                <div style={{ marginLeft: '28px', fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                    Dock 4, Logistics Way, Houston
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Approval History Section */}
                    <div className={styles['form-section']}>
                        <h3>Approval History</h3>
                        <div className={styles['timeline']}>
                            <div className={styles['timeline-item']}>
                                <div className={`${styles['timeline-icon']} ${styles['success']}`}>
                                    <MdCheckCircle size={24} />
                                </div>
                                <div className={styles['timeline-content']}>
                                    <h4>PO Created</h4>
                                    <p>Oct 24, 09:42 AM by Alexander M.</p>
                                </div>
                            </div>
                            <div className={styles['timeline-item']}>
                                <div className={`${styles['timeline-icon']} ${styles['success']}`}>
                                    <MdCheckCircle size={24} />
                                </div>
                                <div className={styles['timeline-content']}>
                                    <h4>Budget Verified</h4>
                                    <p>Oct 24, 11:15 AM by Finance Bot</p>
                                </div>
                            </div>
                            <div className={styles['timeline-item']}>
                                <div className={`${styles['timeline-icon']} ${styles['pending']}`}>
                                    <MdRadioButtonChecked size={24} />
                                </div>
                                <div className={styles['timeline-content']}>
                                    <h4>Dept. Approval Pending</h4>
                                    <p>Assigned to Elena Rodriguez</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PODetailView;
